// Extracted from CaseStudyVisitorSystem.jsx to reduce component complexity.
// Bilingual content (DE/EN) for the Visitor Instruction System case study page.

export const seoContent = {
  de: {
    title: 'Case Study Besucherunterweisungssystem - Fremdfirmenmanagement',
    description: 'Praxisbeispiel: Wie ein Industrieunternehmen Besucher- und Fremdfirmenunterweisungen digitalisiert hat. Von manuell zu automatisiert. Zutrittskontrolle, Dokumentation.',
    keywords: 'Case Study Besucherunterweisungssystem, Fremdfirmenunterweisung digitalisieren, Besuchermanagement Praxisbeispiel, Kontraktoren Unterweisung, Sicherheitsunterweisung Besucher'
  },
  en: {
    title: 'Case Study Visitor Instruction System - Contractor Management',
    description: 'Practical example: How an industrial company digitized visitor and contractor instructions. From manual to automated. Access control, documentation.',
    keywords: 'Case Study Visitor instruction system, digitize contractor training, visitor management practical example, contractor instruction, safety instruction visitors'
  }
};
export const content = {
  de: {
    title: 'Case Study',
    subtitle: 'Digitales Besucher- und Fremdfirmenunterweisungssystem',
    backLink: 'Zurück zum Portfolio',
    
    situation: {
      title: 'Ausgangssituation',
      text: 'Ein Industrieunternehmen mit mehreren Produktionsstandorten empfängt regelmäßig externe Besucher, Lieferanten, Monteure und Fremdfirmen. Vor dem Betreten des Werksgeländes müssen diese über die geltenden Sicherheitsregeln und Verhaltensvorschriften informiert werden. Bisher wurden diese Sicherheitsunterweisungen vor Ort durchgeführt, meist am Empfang oder durch Sicherheitsbeauftragte. Dieser Prozess brachte mehrere Herausforderungen mit sich:',
      points: [
        'Besucher mussten vor Ort auf die Sicherheitsunterweisung warten',
        'Mitarbeiter mussten regelmäßig Unterweisungen durchführen',
        'Sicherheitsinformationen wurden teilweise unterschiedlich vermittelt',
        'Die Dokumentation erfolgte manuell über Listen oder Formulare',
        'Bei hoher Besucherfrequenz entstand zusätzlicher Organisationsaufwand'
      ],
      conclusion: 'Dies führte zu ineffizienten Abläufen am Empfang, insbesondere an größeren Produktionsstandorten mit vielen externen Personen pro Tag.'
    },
    
    challenge: {
      title: 'Herausforderung',
      text: 'Das Unternehmen suchte eine Lösung, um:',
      points: [
        'Besucher effizient über Sicherheitsregeln zu informieren',
        'Wartezeiten am Empfang zu reduzieren',
        'Standardisierte Sicherheitsunterweisungen sicherzustellen',
        'Die Durchführung der Unterweisungen zuverlässig zu dokumentieren',
        'Die Zutrittsprozesse für Besucher und Fremdfirmen zu optimieren',
        'Den organisatorischen Aufwand für Mitarbeiter zu reduzieren'
      ],
      extra: 'Zusätzlich sollte die Lösung in bestehende Systeme und Prozesse integriert werden können.'
    },
    
    solution: {
      title: 'Lösung',
      text: 'In Zusammenarbeit mit e-matrix wurde ein digitales Besucher- und Fremdfirmenunterweisungssystem umgesetzt. Besucher erhalten vor ihrem Termin einen Link zur digitalen Sicherheitsunterweisung und können diese online vor dem Besuch absolvieren.',
      subtitle: 'Die digitale Unterweisung umfasst:',
      points: [
        'Einführung in die wichtigsten Sicherheitsregeln des Unternehmens',
        'Anschauliche Inhalte mit Bildern und kurzen Videos',
        'Einen kurzen Wissenstest am Ende der Schulung'
      ],
      conclusion: 'Nach erfolgreichem Abschluss wird die Unterweisung automatisch dokumentiert. Beim Betreten des Unternehmens kann sofort überprüft werden, ob die Unterweisung bereits absolviert wurde.'
    },

    integration: {
      title: 'Integration in das Zutrittssystem',
      text: 'Das System wurde zudem mit dem bestehenden Schließ- und Zutrittssystem des Unternehmens verbunden. Dadurch wurden mehrere Prozesse automatisiert:',
      points: [
        'Besucherausweise können automatisch vorbereitet und gedruckt werden',
        'Zutrittsberechtigungen können vorab definiert werden',
        'Fremdfirmen erhalten nur Zugang zu festgelegten Bereichen',
        'Der Empfang sieht sofort, ob die Sicherheitsunterweisung bereits absolviert wurde'
      ],
      conclusion: 'Der gesamte Besucherprozess wurde dadurch deutlich effizienter.'
    },

    multisite: {
      title: 'Mehrstufige Besucherunterweisung für mehrere Standorte',
      text: 'Da das Unternehmen mehrere Standorte betreibt, wurde das System mit einer zweigeteilten Sicherheitsunterweisung konzipiert:',
      general: {
        title: 'Allgemeiner Teil (unternehmensweit)',
        text: 'Dieser Teil enthält grundlegende Sicherheitsregeln, die für alle Standorte gelten:',
        points: [
          'Allgemeine Sicherheitsregeln',
          'Verhalten im Notfall',
          'Persönliche Schutzausrüstung',
          'Grundlegende Verhaltensregeln am Standort'
        ],
        note: 'Dieser Teil muss von Besuchern nur einmal absolviert werden.'
      },
      specific: {
        title: 'Standortspezifischer Teil',
        text: 'Zusätzlich zum allgemeinen Teil gibt es für jeden Standort einen eigenen Abschnitt mit spezifischen Informationen:',
        points: [
          'Besondere Gefahren am Standort',
          'Spezifische Sicherheitsregeln',
          'Standortspezifische Abläufe',
          'Relevante Zutrittsbereiche'
        ]
      },
      efficiency: {
        title: 'Effiziente Wiederverwendung der Schulung',
        text: 'Wenn ein Besucher einen Standort zum ersten Mal besucht, absolviert er den allgemeinen Teil und den standortspezifischen Teil. Besucht dieselbe Person später einen anderen Standort des Unternehmens, muss nur der standortspezifische Teil für diesen neuen Standort absolviert werden. Der allgemeine Teil bleibt gültig.'
      }
    },
    
    result: {
      title: 'Ergebnis',
      text: 'Die Einführung der digitalen Besucherunterweisung führte zu mehreren Verbesserungen:',
      items: [
        { title: 'Effizientere Abläufe am Empfang', text: 'Besucher können die Sicherheitsunterweisung vor ihrem Besuch absolvieren – Wartezeiten werden reduziert.' },
        { title: 'Entlastung der Mitarbeiter', text: 'Mitarbeiter müssen keine wiederkehrenden Unterweisungen vor Ort mehr durchführen.' },
        { title: 'Automatisierter Zutrittsprozess', text: 'Besucherausweise und Zutrittsberechtigungen können vorab vorbereitet werden.' },
        { title: 'Standardisierte Sicherheitsinformation', text: 'Alle Besucher erhalten exakt die gleichen Sicherheitsinformationen.' },
        { title: 'Digitale Dokumentation', text: 'Absolvierte Sicherheitsunterweisungen werden automatisch dokumentiert und sind jederzeit nachweisbar.' },
        { title: 'Effiziente Lösung für mehrere Standorte', text: 'Besucher müssen den allgemeinen Teil nur einmal absolvieren – bei weiteren Standortbesuchen nur den jeweiligen standortspezifischen Teil.' }
      ]
    },
    
    comparison: {
      title: 'Ergebnis im Überblick',
      before: {
        title: 'Vorher',
        points: [
          'Persönliche Sicherheitsunterweisungen vor Ort',
          'Wartezeiten am Empfang',
          'Manuelle Dokumentation',
          'Hoher organisatorischer Aufwand',
          'Unterschiedliche Sicherheitsinformationen'
        ]
      },
      after: {
        title: 'Nachher',
        points: [
          'Digitale Besucherunterweisung',
          'Schnellere Empfangsprozesse',
          'Automatische Dokumentation',
          'Integration ins Zutrittssystem',
          'Definierte Zutrittsbereiche für Fremdfirmen',
          'Effiziente Lösung für mehrere Standorte'
        ]
      }
    },
    
    cta: {
      title: 'Ähnliche Herausforderungen?',
      text: 'Kontaktieren Sie uns für eine individuelle Beratung und erfahren Sie, wie wir auch Ihr Unternehmen unterstützen können.',
      button: 'Kontakt aufnehmen',
      systemLink: 'Mehr zum Besucherunterweisungssystem'
    }
  },
  en: {
    title: 'Case Study',
    subtitle: 'Digital Visitor and Third-Party Company Instruction System',
    backLink: 'Back to Portfolio',
    
    situation: {
      title: 'Initial Situation',
      text: 'An industrial company with multiple production sites regularly receives external visitors, suppliers, installers, and third-party companies. Before entering the factory premises, these individuals must be informed about the applicable safety rules and behavioral regulations. Previously, these safety instructions were conducted on-site, usually at reception or by safety officers. This process presented several challenges:',
      points: [
        'Visitors had to wait on-site for safety instructions',
        'Employees had to conduct instructions regularly',
        'Safety information was sometimes conveyed differently',
        'Documentation was done manually using lists or forms',
        'High visitor frequency resulted in additional organizational effort'
      ],
      conclusion: 'This led to inefficient reception processes, especially at larger production sites with many external individuals per day.'
    },
    
    challenge: {
      title: 'Challenge',
      text: 'The company sought a solution to:',
      points: [
        'Efficiently inform visitors about safety rules',
        'Reduce waiting times at reception',
        'Ensure standardized safety instructions',
        'Reliably document the completion of instructions',
        'Optimize access processes for visitors and third-party companies',
        'Reduce the organizational effort for employees'
      ],
      extra: 'Additionally, the solution should integrate with existing systems and processes.'
    },
    
    solution: {
      title: 'Solution',
      text: 'In collaboration with e-matrix, a digital visitor and third-party company instruction system was implemented. Visitors receive a link to the digital safety instruction before their appointment and can complete it online before their visit.',
      subtitle: 'The digital instruction includes:',
      points: [
        'Introduction to the company\'s most important safety rules',
        'Illustrative content with images and short videos',
        'A brief knowledge check at the end of the training'
      ],
      conclusion: 'Upon successful completion, the instruction is automatically documented. Upon entering the company, it can be immediately verified whether the instruction has already been completed.'
    },

    integration: {
      title: 'Integration into the Access System',
      text: 'The system was also connected to the company\'s existing locking and access system. This automated several processes:',
      points: [
        'Visitor passes can be automatically prepared and printed',
        'Access rights can be defined in advance',
        'Third-party companies only receive access to designated areas',
        'Reception can immediately see if the safety instruction has been completed'
      ],
      conclusion: 'This made the entire visitor process significantly more efficient.'
    },

    multisite: {
      title: 'Multi-Level Visitor Instruction for Multiple Sites',
      text: 'Since the company operates multiple sites, the system was designed with a two-part safety instruction:',
      general: {
        title: 'General Part (Company-wide)',
        text: 'This part contains basic safety rules applicable to all sites:',
        points: [
          'General safety rules',
          'Behavior in emergencies',
          'Personal protective equipment',
          'Basic behavioral rules on-site'
        ],
        note: 'This part only needs to be completed by visitors once.'
      },
      specific: {
        title: 'Site-Specific Part',
        text: 'In addition to the general part, each site has its own section with specific information:',
        points: [
          'Particular hazards at the site',
          'Specific safety rules',
          'Site-specific procedures',
          'Relevant access areas'
        ]
      },
      efficiency: {
        title: 'Efficient Re-use of Training',
        text: 'When a visitor visits a site for the first time, they complete the general part and the site-specific part. If the same person later visits another site of the company, only the site-specific part for that new location needs to be completed. The general part remains valid.'
      }
    },
    
    result: {
      title: 'Result',
      text: 'The introduction of the digital visitor instruction led to several improvements:',
      items: [
        { title: 'More Efficient Reception Processes', text: 'Visitors can complete the safety instruction before their visit – reducing waiting times.' },
        { title: 'Relief for Employees', text: 'Employees no longer need to conduct recurring on-site instructions.' },
        { title: 'Automated Access Process', text: 'Visitor passes and access rights can be prepared in advance.' },
        { title: 'Standardized Safety Information', text: 'All visitors receive exactly the same safety information.' },
        { title: 'Digital Documentation', text: 'Completed safety instructions are automatically documented and can be proven at any time.' },
        { title: 'Efficient Solution for Multiple Sites', text: 'Visitors only need to complete the general part once – only the site-specific part for subsequent visits.' }
      ]
    },
    
    comparison: {
      title: 'Results at a Glance',
      before: {
        title: 'Before',
        points: [
          'On-site personal safety instructions',
          'Waiting times at reception',
          'Manual documentation',
          'High organizational effort',
          'Divergent safety information'
        ]
      },
      after: {
        title: 'After',
        points: [
          'Digital visitor instruction',
          'Faster reception processes',
          'Automatic documentation',
          'Integration into access system',
          'Defined access areas for third-party companies',
          'Efficient solution for multiple sites'
        ]
      }
    },
    
    cta: {
      title: 'Facing Similar Challenges?',
      text: 'Contact us for individual consultation and learn how we can support your company too.',
      button: 'Contact Us',
      systemLink: 'Learn more about the Visitor Instruction System'
    }
  }
};

